const STATUS_LABELS = {
  ONLINE: 'Online',
  AVAILABLE: 'Available',
  AWAY: 'Away',
  LONG_AWAY: 'Long Away',
  OFFLINE: 'Offline',
  LOGGED_OUT: 'Logged Out',
  ON_LEAVE: 'Out of Office',
  BUSY: 'Busy',
  DO_NOT_DISTURB: 'Do not Disturb',
  SICK_LEAVE: 'Sick Leave',
  HOLIDAY: 'Holiday',
  HAVING_FOOD: 'Having Food',
  WORK_FROM_HOME: 'Available',
  WORK_FROM_OFFICE: 'Available',
  IN_A_CALL: 'In a call',
};

const EXCLUSIVE_STATUSES = new Set(['LOGGED_OUT', 'SICK_LEAVE', 'IN_A_CALL']);

const normalizeStatus = (status) => {
  if (status == null) return null;
  if (typeof status === 'string') return status.toUpperCase();
  if (typeof status === 'object') {
    const raw = status.name || status.status || status.value;
    return raw != null ? String(raw).toUpperCase() : null;
  }
  return String(status).toUpperCase();
};

export const resolveWorkLocation = (user) => {
  const loc = normalizeStatus(user?.workLocation);
  if (loc === 'WORK_FROM_HOME' || loc === 'WORK_FROM_OFFICE') return loc;
  const status = normalizeStatus(user?.status);
  if (status === 'WORK_FROM_HOME' || status === 'WORK_FROM_OFFICE') return status;
  return null;
};

export const canonicalPresenceStatus = (status) => {
  const value = normalizeStatus(status);
  if (value === 'WORK_FROM_HOME' || value === 'WORK_FROM_OFFICE') return 'AVAILABLE';
  return value;
};

export const isOnBreakPresence = (user) => {
  if (!user) return false;
  // Explicit false from live sync must win (join-back).
  if (user.onBreak === false) return false;
  if (user.onBreak === true) return true;
  // Legacy heuristic only when onBreak flag is unknown.
  return user.statusSource === 'ATTENDANCE' && normalizeStatus(user.status) === 'AWAY';
};

export const formatPresenceLabel = (user) => {
  if (!user) return 'Offline';
  const rawStatus = normalizeStatus(user.status);

  if (rawStatus === 'ON_LEAVE') {
    const leaveLabel = user.statusMessage || 'Full day leave';
    return leaveLabel.includes('leave') || leaveLabel.includes('Leave')
      ? leaveLabel
      : `Out of Office · ${leaveLabel}`;
  }

  if (isOnBreakPresence(user)) {
    const reason = user.statusMessage || user.breakReason;
    return reason ? `On break · ${reason}` : 'On break';
  }

  if (EXCLUSIVE_STATUSES.has(rawStatus)) {
    return STATUS_LABELS[rawStatus] || rawStatus;
  }

  const status = canonicalPresenceStatus(rawStatus);
  const base = STATUS_LABELS[status] || 'Offline';
  const location = resolveWorkLocation(user);
  const locationSuffix = location === 'WORK_FROM_HOME'
    ? ' (work from home)'
    : location === 'WORK_FROM_OFFICE'
      ? ' (work from office)'
      : '';
  const label = `${base}${locationSuffix}`;
  if (user.statusMessage) return `${label} · ${user.statusMessage}`;
  return label;
};

export const presenceDotClass = (userOrStatus) => {
  const user = userOrStatus && typeof userOrStatus === 'object'
    && (userOrStatus.status != null || userOrStatus.workLocation != null || userOrStatus.presenceStatus != null
      || userOrStatus.onBreak != null || userOrStatus.statusSource != null)
    ? {
      status: userOrStatus.status || userOrStatus.presenceStatus,
      workLocation: userOrStatus.workLocation,
      onBreak: userOrStatus.onBreak,
      statusSource: userOrStatus.statusSource,
    }
    : { status: userOrStatus };
  const status = canonicalPresenceStatus(user.status);
  if (EXCLUSIVE_STATUSES.has(normalizeStatus(user.status))) {
    const raw = normalizeStatus(user.status);
    if (raw === 'IN_A_CALL') return 'bg-red-500';
    return raw === 'SICK_LEAVE' ? 'bg-orange-500' : 'bg-slate-500';
  }
  if (status === 'IN_A_CALL' || normalizeStatus(user.status) === 'IN_A_CALL') {
    return 'bg-red-500';
  }
  if (isOnBreakPresence(user) || status === 'AWAY') return 'bg-yellow-500';
  if (status === 'LONG_AWAY') return 'bg-amber-500';
  const location = resolveWorkLocation(user);
  if (location === 'WORK_FROM_HOME') return 'bg-emerald-500';
  if (location === 'WORK_FROM_OFFICE') return 'bg-sky-500';
  if (['ONLINE', 'AVAILABLE'].includes(status)) return 'bg-green-500';
  if (status === 'LOGGED_OUT') return 'bg-slate-500';
  if (status === 'ON_LEAVE') return 'bg-indigo-500';
  if (['BUSY', 'DO_NOT_DISTURB'].includes(status)) return 'bg-red-500';
  if (status === 'SICK_LEAVE') return 'bg-orange-500';
  if (status === 'HOLIDAY') return 'bg-blue-500';
  if (status === 'HAVING_FOOD') return 'bg-amber-500';
  return 'bg-gray-400';
};

export const buildPresencePatchFromStatusUpdate = (data) => {
  if (!data?.userId) return null;
  const status = canonicalPresenceStatus(data.status) || normalizeStatus(data.status);
  const onBreak = data.onBreak === true;
  const workLocation = resolveWorkLocation({
    status: data.status,
    workLocation: data.workLocation,
  });
  const user = {
    status,
    workLocation,
    statusMessage: data.statusMessage ?? null,
    statusSource: data.statusSource || null,
    onBreak,
  };

  const patch = {
    presenceStatus: status,
    workLocation,
    statusMessage: data.statusMessage ?? null,
    onBreak,
    presenceLabel: formatPresenceLabel(user),
  };

  if (onBreak) {
    patch.attendanceState = 'ON_BREAK';
    patch.checkedIn = true;
    patch.loggedOut = false;
  } else if (status === 'AVAILABLE' || status === 'ONLINE') {
    // Clear sticky break badge when user joins back / becomes available.
    patch.attendanceState = 'CHECKED_IN';
    patch.checkedIn = true;
    patch.loggedOut = false;
  } else if (status === 'LOGGED_OUT') {
    patch.attendanceState = 'LOGGED_OUT';
    patch.checkedIn = false;
    patch.loggedOut = true;
  } else if (status === 'ON_LEAVE') {
    patch.attendanceState = 'ON_LEAVE';
    patch.onBreak = false;
    patch.checkedIn = false;
    patch.loggedOut = false;
  }

  return { userId: data.userId, patch };
};

export const buildPresencePatchFromAttendanceEvent = (data) => {
  const userId = data?.eventUserId || data?.senderId;
  if (!userId) return null;

  const eventType = String(data.attendanceEventType || '').toUpperCase();
  if (eventType === 'LEAVE_APPLY' || eventType === 'LEAVE_APPROVED' || data.attendanceState === 'ON_LEAVE') {
    const statusMessage = data.statusMessage || data.presenceLabel || 'On leave';
    return {
      userId,
      attendanceEventType: data.attendanceEventType,
      patch: {
        presenceStatus: 'ON_LEAVE',
        statusMessage,
        onBreak: false,
        attendanceState: 'ON_LEAVE',
        presenceLabel: data.presenceLabel || statusMessage,
      },
    };
  }
  const isBreakEnd = eventType === 'BREAK_END'
    || data.attendanceState === 'CHECKED_IN'
    || data.onBreak === false;
  const isBreakStart = !isBreakEnd && (
    eventType === 'BREAK_START'
    || data.attendanceState === 'ON_BREAK'
    || data.onBreak === true
  );
  const onBreak = Boolean(isBreakStart);

  // Never keep break reason as statusMessage after join-back.
  const statusMessage = onBreak
    ? (data.statusMessage ?? data.breakReason ?? null)
    : null;

  const presenceStatus = normalizeStatus(data.presenceStatus)
    || (onBreak ? 'AWAY' : (isBreakEnd ? 'AVAILABLE' : null));

  const patch = {
    presenceStatus,
    statusMessage,
    onBreak,
  };

  if (onBreak) {
    patch.attendanceState = 'ON_BREAK';
    patch.checkedIn = true;
    patch.loggedOut = false;
  } else if (isBreakEnd) {
    patch.attendanceState = data.attendanceState || 'CHECKED_IN';
    patch.checkedIn = true;
    patch.loggedOut = false;
  } else if (data.attendanceState) {
    patch.attendanceState = data.attendanceState;
    patch.checkedIn = data.attendanceState === 'CHECKED_IN' || data.attendanceState === 'ON_BREAK';
    patch.loggedOut = data.attendanceState === 'LOGGED_OUT';
  }

  patch.presenceLabel = (isBreakEnd && !onBreak)
    ? (data.presenceLabel || formatPresenceLabel({
      status: presenceStatus || 'AVAILABLE',
      workLocation: data.workLocation,
      statusMessage: patch.statusMessage,
      onBreak: false,
    }))
    : (data.presenceLabel || formatPresenceLabel({
      status: patch.presenceStatus,
      workLocation: data.workLocation,
      statusMessage: patch.statusMessage,
      statusSource: 'ATTENDANCE',
      onBreak: patch.onBreak,
    }));

  return { userId, patch, attendanceEventType: data.attendanceEventType };
};

export const isPresentStatus = (status) => {
  const value = canonicalPresenceStatus(status);
  return ['ONLINE', 'AVAILABLE'].includes(value);
};
